import { axiosInstance } from "@/plugins/axios";
import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import BaseCommand from "@/models/api/commands/BaseCommand";
import { OrderGetTemporaryOrderItemsResponse } from "@/models/api/orders/temporary/OrderGetTemporaryOrderItems";
import {
  OrderGetTemporaryOwner,
  OrderGetTemporaryOwnersResponse,
} from "@/models/api/orders/temporary/OrderGetTemporaryOwners";
import { OrderGetTemporaryContractors } from "@/models/api/orders/temporary/OrderGetTemporaryContractors";
import { OrderGetTemporaryOrderSection } from "@/models/api/orders/temporary/OrderGetTemporaryOrderSection";
import { OperationResult } from "@/models/api/orders/OperationResult";
import { CalculateValueInCurrency } from "@/models/api/orders/CalculateValueInCurrency";
import {
  OrderGetServiceTypeAttributesRequest,
  OrderGetServiceTypeAttributesResponse,
  ValidateAttributesRequest,
} from "@/models/api/orders/temporary/OrderGetServiceTypeAttributes";
import { ContractorMonit, OrderMonit } from "@/models/api/orders/temporary/OrderGetTemporaryMonit";
import {
  OrderGetTemporaryCalculateValueRequest,
  OrderGetTemporaryCalculateValueResponse,
} from "@/models/api/orders/temporary/OrderGetTemporaryCalculateValue";
import {
  ContractorAvailableProject,
  OrderGetContractorAvailableProjects,
} from "@/models/api/orders/temporary/OrderGetContractorAvailableProjects";
import { ORDER_EDITOR_REASON } from "@/static/orderEditorReason";
import { BookingItemToImport } from "@/models/api/orders/BookingItemToImport";
import { CorrectionReasonTemporary } from "@/models/api/orders/temporary/OrderGetTemporaryCorrectionReason";
import { Owner } from "@/models/api/Owner";

//#region Routes
export const URL_POST_EDIT_ORDER = "/Order/${OrderId}/EditOrder";
export const URL_POST_CLONE_ORDER = "/Order/${OrderId}/CloneOrder";
export const URL_POST_EDIT_DOCUMENT = "/Document/${DocumentId}/Edit";
export const URL_POST_NEW_ORDER = "/Order/null/EditOrder";
export const URL_POST_UPDATE_ORDER_TEMPORARY = "/Order/UpdateOrderTemporary";
export const URL_GET_TEMPORARY_CONTRACTORS = "/Order/${OperationId}/TemporaryContractors";
export const URL_GET_TEMPORARY_ORDER_ITEMS_PAGED = "/Order/${OperationId}/TemporaryOrderItems/Paged";
export const URL_GET_TEMPORARY_OWNERS = "/Order/${OperationId}/TemporaryOwners";
export const URL_GET_TEMPORARY_ORDER = "/Order/${OperationId}/TemporaryOrder";
export const URL_GET_CONTRACTOR_AVAILABLE_PROJECTS = "/Order/${OperationId}/ContractorAvailableProjects";
export const URL_GET_BOOKING_ITEMS_TO_CONVERT = "/Order/${OperationId}/BookingItemsToConvert";
export const URL_GET_TEMPORARY_COMMENT = "/Order/${OperationId}/TemporaryComment";
export const URL_GET_TEMPORARY_CORRECTION_REASON = "/Order/${OperationId}/TemporaryCorrectionReason";
export const URL_GET_TEMPORARY_CHECK_SERVICE_TYPE_BEFORE_ADD_TO_ALL =
  "/Order/${OperationId}/CheckServiceTypBeforeAddToAll";
export const URL_POST_SET_TEMPORARY_ON_ORDER = "/Order/${OrderId}/SetTemporaryOrder";
export const URL_POST_SET_TEMPORARY_ON_ORDER_IN_ORDER_TABLE = "/Order/${OrderId}/SetTemporaryOnOrderInOrderTable";
export const URL_GET_CALCULATE_VALUE_IN_CURRENCY = "/Order/CalculateValueFromValueInCurrency";
export const URL_GET_SERVICE_TYPE_ATTRIBUTES_FOR_ORDER_ITEM_EDIT =
  "/Order/${OperationId}/ServiceTypeAttributesForOrderItemEdit";
export const URL_POST_ORDER_VALIDATE_ATTRIBUTES = "/Order/${OperationId}/ValidateAttributes";
export const URL_GET_TEMPORARY_ORDER_CONTRACTOR_MONIT = "/Order/${OperationId}/TemporaryContractorMonit";
export const URL_GET_UNAVAILABLE_CASHIERS = "/Order/${OperationId}/UnavailableCashiers";
export const URL_GET_TEMPORARY_ORDER_ORDER_ITEM_MONIT = "/Order/${OperationId}/TemporaryOrderItemMonit";
export const URL_GET_TEMPORARY_ORDER_CALCULATE_VALUE = "/Order/CalculateOrderItemValues";
export const URL_POST_SAVE_ORDER = "/Order/${OperationId}/SaveOrder";
export const URL_GET_CHECK_REQUIRE_DATA_TO_GENERATE_DOCUMENT =
  "/Order/${OperationId}/CheckRequireDataToGenerateDocument";
export const URL_GET_IS_ADVANCE_DOCUMENT_LARGER_AMOUNT_THAN_ORDER =
  "/Order/${OperationId}/IsAdvanceDocumentLargerAmountThanOrder";
//#endregion

export const editOrder = async (orderId: string | null, reason: ORDER_EDITOR_REASON): Promise<string> => {
  const response = await axiosInstance.post(
    orderId === null ? URL_POST_NEW_ORDER : URL_POST_EDIT_ORDER,
    { reason: reason },
    { params: { OrderId: orderId, reason: reason } }
  );
  return response.data;
};

const _cloneOrder = new CancellableHelper<string>(URL_POST_CLONE_ORDER, "string");
export const cloneOrder = async (orderId: string): Promise<string> => {
  return await _cloneOrder.post({
    OrderId: orderId,
  });
};

const _editDocument = new CancellableHelper<string>(URL_POST_EDIT_DOCUMENT, "string");
export const editDocument = async (documentId: string): Promise<string> => {
  return await _editDocument.post({
    DocumentId: documentId,
  });
};

export const updateOrderTemporary = async (command: BaseCommand): Promise<OperationResult> => {
  const _updateOrderTemporary = new CancellableHelper<OperationResult>(
    URL_POST_UPDATE_ORDER_TEMPORARY,
    OperationResult
  );
  let response = null;
  try {
    response = await _updateOrderTemporary.post({}, command);
  } catch (e) {
    if (typeof command.AfterExecuteCallback === "function") {
      command.AfterExecuteCallback(e);
    }
    throw e;
  }
  if (typeof command.AfterExecuteCallback === "function") {
    command.AfterExecuteCallback(response);
  }
  return response as OperationResult;
};

// add edit order data
const _getTemporaryContractors = new CancellableHelper<OrderGetTemporaryContractors>(
  URL_GET_TEMPORARY_CONTRACTORS,
  OrderGetTemporaryContractors
);
export const getTemporaryContractors = async (operationId: string): Promise<OrderGetTemporaryContractors> => {
  return await _getTemporaryContractors.get({
    OperationId: operationId,
  });
};

const _getTemporaryOrderItems = new CancellableHelper<OrderGetTemporaryOrderItemsResponse>(
  URL_GET_TEMPORARY_ORDER_ITEMS_PAGED,
  OrderGetTemporaryOrderItemsResponse
);
_getTemporaryOrderItems.jsonSerializer = true;
export const getTemporaryOrderItems = async (
  operationId: string,
  genericTableRequest: GenericTableRequest
): Promise<OrderGetTemporaryOrderItemsResponse> => {
  return await _getTemporaryOrderItems.get({
    OperationId: operationId,
    ...genericTableRequest,
  });
};

const _getTemporaryOwners = new CancellableHelper<OrderGetTemporaryOwnersResponse>(
  URL_GET_TEMPORARY_OWNERS,
  OrderGetTemporaryOwner
);
export const getTemporaryOwners = async (operationId: string): Promise<OrderGetTemporaryOwnersResponse> => {
  return await _getTemporaryOwners.get({
    OperationId: operationId,
  });
};

const _getUnavailableCashiers = new CancellableHelper<Array<Owner>>(URL_GET_UNAVAILABLE_CASHIERS, Owner);
export const getUnavailableCashiers = async (operationId: string): Promise<Array<Owner>> => {
  return await _getUnavailableCashiers.get({
    OperationId: operationId,
  });
};

const _getTemporaryComment = new CancellableHelper<string>(URL_GET_TEMPORARY_COMMENT);
export const getTemporaryComment = async (operationId: string): Promise<string> => {
  return await _getTemporaryComment.get({
    OperationId: operationId,
  });
};

const _getTemporaryCorrectionReason = new CancellableHelper<CorrectionReasonTemporary>(
  URL_GET_TEMPORARY_CORRECTION_REASON,
  CorrectionReasonTemporary
);
export const getTemporaryCorrectionReason = async (operationId: string): Promise<CorrectionReasonTemporary> => {
  return await _getTemporaryCorrectionReason.get({
    OperationId: operationId,
  });
};

const _getTemporaryOrder = new CancellableHelper<OrderGetTemporaryOrderSection>(
  URL_GET_TEMPORARY_ORDER,
  OrderGetTemporaryOrderSection
);
export const getTemporaryOrder = async (operationId: string): Promise<OrderGetTemporaryOrderSection> => {
  return await _getTemporaryOrder.get({
    OperationId: operationId,
  });
};

const _getContractorAvailableProjects = new CancellableHelper<OrderGetContractorAvailableProjects>(
  URL_GET_CONTRACTOR_AVAILABLE_PROJECTS,
  ContractorAvailableProject
);
export const getContractorAvailableProjects = async (
  operationId: string
): Promise<OrderGetContractorAvailableProjects> => {
  return await _getContractorAvailableProjects.get({
    OperationId: operationId,
  });
};

const _getBookingItemsToConvert = new CancellableHelper<Array<BookingItemToImport>>(
  URL_GET_BOOKING_ITEMS_TO_CONVERT,
  BookingItemToImport
);
export const getBookingItemsToConvert = async (operationId: string): Promise<Array<BookingItemToImport>> => {
  return await _getBookingItemsToConvert.get({
    OperationId: operationId,
  });
};

const _getCheckServiceTypeBeforeAddToAll = new CancellableHelper<string>(
  URL_GET_TEMPORARY_CHECK_SERVICE_TYPE_BEFORE_ADD_TO_ALL
);
export const getCheckServiceTypeBeforeAddToAll = async (operationId: string): Promise<string> => {
  return await _getCheckServiceTypeBeforeAddToAll.get({
    OperationId: operationId,
  });
};

const _setTemporaryOnOrder = new CancellableHelper<string>(URL_POST_SET_TEMPORARY_ON_ORDER);
export const setTemporaryOnOrder = async (orderId: string, flag: boolean): Promise<string> => {
  return await _setTemporaryOnOrder.post({
    OrderId: orderId,
    Flag: flag,
  });
};

const _setTemporaryOnOrderInOrderTable = new CancellableHelper<string>(URL_POST_SET_TEMPORARY_ON_ORDER_IN_ORDER_TABLE);
export const setTemporaryOnOrderInOrderTable = async (orderId: string, flag: boolean): Promise<string> => {
  return await _setTemporaryOnOrderInOrderTable.post({
    OrderId: orderId,
    Flag: flag,
  });
};

const _getCalculatedValue = new CancellableHelper<OrderGetTemporaryCalculateValueResponse>(
  URL_GET_TEMPORARY_ORDER_CALCULATE_VALUE,
  OrderGetTemporaryCalculateValueResponse
);
export const getCalculatedValue = async (
  param: OrderGetTemporaryCalculateValueRequest
): Promise<OrderGetTemporaryCalculateValueResponse> => {
  return await _getCalculatedValue.post(param);
};

export const getCalculatedValueInCurrency = async (params: CalculateValueInCurrency): Promise<number> => {
  const result = await axiosInstance.get(URL_GET_CALCULATE_VALUE_IN_CURRENCY, {
    params: params,
  });
  return result.data;
};

const _getAttributesForOrderItem = new CancellableHelper<OrderGetServiceTypeAttributesResponse>(
  URL_GET_SERVICE_TYPE_ATTRIBUTES_FOR_ORDER_ITEM_EDIT
);
export const getAttributesForOrderItem = async (
  operationId: string,
  param: OrderGetServiceTypeAttributesRequest
): Promise<OrderGetServiceTypeAttributesResponse> => {
  return await _getAttributesForOrderItem.get({
    OperationId: operationId,
    ...param,
  });
};

export const postValidateAttributes = async (
  operationId: string,
  param: ValidateAttributesRequest,
  skipValidation: boolean
): Promise<boolean> => {
  const response = await axiosInstance.post(URL_POST_ORDER_VALIDATE_ATTRIBUTES, param, {
    params: { OperationId: operationId, SkipValidation: skipValidation },
  });
  return response.status === 200;
};

const _getTemporaryContractorMonit = new CancellableHelper<Array<ContractorMonit>>(
  URL_GET_TEMPORARY_ORDER_CONTRACTOR_MONIT,
  ContractorMonit,
  true
);
export const getTemporaryContractorMonit = async (operationId: string): Promise<Array<ContractorMonit>> => {
  return await _getTemporaryContractorMonit.get({
    OperationId: operationId,
  });
};

const _getTemporaryOrderItemMonit = new CancellableHelper<OrderMonit>(
  URL_GET_TEMPORARY_ORDER_ORDER_ITEM_MONIT,
  OrderMonit,
  true
);
export const getTemporaryOrderItemMonit = async (operationId: string): Promise<OrderMonit> => {
  return await _getTemporaryOrderItemMonit.get({
    OperationId: operationId,
  });
};

const _postSaveOrder = new CancellableHelper<string>(URL_POST_SAVE_ORDER);
export const postSaveOrder = async (operationId: string): Promise<string> => {
  return await _postSaveOrder.post({
    OperationId: operationId,
  });
};

const _getIsAdvanceDocumentLargerAmountThanOrder = new CancellableHelper<boolean>(
  URL_GET_IS_ADVANCE_DOCUMENT_LARGER_AMOUNT_THAN_ORDER
);
export const getIsAdvanceDocumentLargerAmountThanOrder = async (
  operationId: string,
  advanceDocumentIds: Array<string>
): Promise<boolean> => {
  return await _getIsAdvanceDocumentLargerAmountThanOrder.get({
    OperationId: operationId,
    advanceDocumentIds: advanceDocumentIds,
  });
};

const _checkRequireDataToGenerateDocument = new CancellableHelper<boolean>(
  URL_GET_CHECK_REQUIRE_DATA_TO_GENERATE_DOCUMENT
);
export const checkRequireDataToGenerateDocument = async (operationId: string): Promise<boolean> => {
  return await _checkRequireDataToGenerateDocument.get({
    OperationId: operationId,
  });
};
